import { ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../../api/client.js';

type LoginLogProvider = 'password' | 'dingtalk';
type LoginLogResult = 'success' | 'failure';

interface LoginLogRow {
  login_log_id: string;
  occurred_at: string;
  systemuserid: string | null;
  username: string | null;
  provider: LoginLogProvider;
  result: LoginLogResult;
  ip_address: string | null;
  user_agent: string | null;
  failure_reason: string | null;
  client_platform: string | null;
  device_type: string | null;
  login_location: string | null;
  device_id: string | null;
  user_name: string | null;
}

const PLATFORM_LABEL: Record<string, { text: string; color: string }> = {
  'pc-web': { text: 'PC Web', color: 'geekblue' },
  h5: { text: 'H5', color: 'cyan' },
  app: { text: 'App', color: 'purple' },
};

interface ListResp {
  rows: LoginLogRow[];
  total: number;
}

export function LoginLogPage() {
  const [data, setData] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [provider, setProvider] = useState<LoginLogProvider | undefined>();
  const [result, setResult] = useState<LoginLogResult | undefined>();
  const [username, setUsername] = useState<string | undefined>();

  const refresh = useCallback(() => {
    setLoading(true);
    http
      .get<ListResp>('/login-log', {
        params: {
          page,
          pageSize: 30,
          provider,
          result,
          username,
        },
      })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [page, provider, result, username]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const columns: ColumnsType<LoginLogRow> = [
    {
      title: '时间',
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN'),
    },
    {
      title: '结果',
      dataIndex: 'result',
      key: 'result',
      width: 100,
      render: (value: LoginLogResult) => (
        <Tag color={value === 'success' ? 'green' : 'red'}>
          {value === 'success' ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '登录方式',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (value: LoginLogProvider) => (
        <Tag color={value === 'dingtalk' ? 'blue' : 'default'}>
          {value === 'dingtalk' ? '钉钉' : '账号密码'}
        </Tag>
      ),
    },
    {
      title: '用户',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 160,
      render: (_value, row) =>
        row.user_name ??
        row.username ?? <Typography.Text type="secondary">未知用户</Typography.Text>,
    },
    {
      title: '登录账号',
      dataIndex: 'username',
      key: 'username',
      width: 220,
      render: (value: string | null) => value ?? '—',
    },
    {
      title: '登录平台',
      dataIndex: 'client_platform',
      key: 'client_platform',
      width: 110,
      render: (value: string | null) => {
        if (!value) return <Typography.Text type="secondary">—</Typography.Text>;
        const meta = PLATFORM_LABEL[value];
        return <Tag color={meta?.color ?? 'default'}>{meta?.text ?? value}</Tag>;
      },
    },
    {
      title: '设备类型',
      dataIndex: 'device_type',
      key: 'device_type',
      width: 200,
      render: (value: string | null) =>
        value ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 140,
      render: (value: string | null) => value ?? '—',
    },
    {
      title: '登录地址',
      dataIndex: 'login_location',
      key: 'login_location',
      width: 100,
      render: (value: string | null) =>
        value ? (
          <Tag color={value === '公网' ? 'orange' : 'default'}>{value}</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '设备标识',
      dataIndex: 'device_id',
      key: 'device_id',
      width: 160,
      render: (value: string | null) =>
        value ? (
          <Typography.Text style={{ maxWidth: 150 }} ellipsis={{ tooltip: value }}>
            {value}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '失败原因',
      dataIndex: 'failure_reason',
      key: 'failure_reason',
      width: 220,
      render: (value: string | null) =>
        value ? (
          <Typography.Text type="danger">{value}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'User-Agent',
      dataIndex: 'user_agent',
      key: 'user_agent',
      width: 360,
      render: (value: string | null) =>
        value ? (
          <Typography.Text style={{ maxWidth: 340 }} ellipsis={{ tooltip: value }}>
            {value}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            登录日志
          </Typography.Title>
          {data && <Tag color="blue">{data.total} 条</Tag>}
        </Space>
      }
      extra={
        <Space wrap>
          <Select<LoginLogProvider>
            allowClear
            placeholder="登录方式"
            value={provider}
            onChange={(value) => {
              setPage(1);
              setProvider(value);
            }}
            style={{ width: 140 }}
            options={[
              { label: '账号密码', value: 'password' },
              { label: '钉钉', value: 'dingtalk' },
            ]}
          />
          <Select<LoginLogResult>
            allowClear
            placeholder="结果"
            value={result}
            onChange={(value) => {
              setPage(1);
              setResult(value);
            }}
            style={{ width: 120 }}
            options={[
              { label: '成功', value: 'success' },
              { label: '失败', value: 'failure' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索姓名或账号"
            onSearch={(value) => {
              setPage(1);
              setUsername(value.trim() || undefined);
            }}
            style={{ width: 220 }}
          />
          <Button icon={<ReloadOutlined />} onClick={refresh}>
            刷新
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="login_log_id"
        loading={loading}
        columns={columns}
        dataSource={data?.rows ?? []}
        pagination={{
          current: page,
          pageSize: 30,
          total: data?.total ?? 0,
          showTotal: (total) => `共 ${total} 条`,
          onChange: setPage,
        }}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
